import { useEffect, useRef, useState } from 'react';

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * One-shot reveal on first intersection. Returns a ref to attach and the flag to
 * feed `data-visible`. Starts visible when motion is reduced or the browser has
 * no IntersectionObserver, so content is never hidden by a missing capability.
 */
export function useReveal<T extends HTMLElement>(): {
  ref: React.RefObject<T | null>;
  visible: boolean;
} {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(() => prefersReducedMotion());

  useEffect(() => {
    const node = ref.current;
    if (!node || visible) return;

    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        }
      },
      // A shallow bottom margin on purpose: at -12% a section that was already
      // peeking into the fold stayed invisible, which reintroduced as blank
      // space exactly what trimming the hero padding was meant to fix.
      { rootMargin: '0px 0px -5% 0px', threshold: 0 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  return { ref, visible };
}
